/* eslint-disable react/prop-types */
/* eslint-disable react/function-component-definition */

import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Grid,
  Icon,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import BlockIcon from "@mui/icons-material/Block";
import style from "assets/style/Modal";
import ModalTitle from "examples/NewDesign/ModalTitle";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import pxToRem from "assets/theme/functions/pxToRem";
import { Icons, Colors } from "utils/Constants";

const LOAN_TYPE_LABELS = {
  home: "Home Loan",
  personal: "Personal Loan",
  business: "Business Loan",
  car: "Car Loan",
  education: "Education Loan",
  credit: "Credit Card",
  loanAgainstProperty: "Loan Against Property",
  loanAgainstSecurity: "Loan Against Security",
  workingCapital: "Working Capital",
  machinery: "Machinery Loan",
  gold: "Gold Loan",
};

export default function ProcessInquiryModal({
  open,
  handleClose,
  inquiry,
  handleConfirmProcess,
  handleRejectStatus,
  loading,
}) {
  const [selectedLoanType, setSelectedLoanType] = useState("home");
  const [remarks, setRemarks] = useState("");
  const [actionType, setActionType] = useState(null);

  useEffect(() => {
    if (open) {
      setSelectedLoanType(inquiry?.loanType || "home");
      setRemarks("");
      setActionType(null);
    }
  }, [open, inquiry]);

  const handleProcessLead = () => {
    setActionType("process");
    handleConfirmProcess(inquiry, selectedLoanType);
  };

  const handleRejectedByBank = () => {
    setActionType("rejected_bank");
    if (handleRejectStatus) {
      handleRejectStatus(inquiry, "rejected by bank", remarks);
    }
  };

  const handleDeniedByCustomer = () => {
    setActionType("denied_customer");
    if (handleRejectStatus) {
      handleRejectStatus(inquiry, "denied by customer", remarks);
    }
  };

  const isBusy = Boolean(loading || actionType);

  return (
    <Modal
      open={open}
      onClose={isBusy ? undefined : handleClose}
      aria-labelledby="process-inquiry-modal-title"
    >
      <MDBox
        sx={{
          ...style,
          minWidth: { xs: "92vw", sm: 620 },
          maxWidth: 680,
          borderRadius: pxToRem(10),
          overflow: "hidden",
          boxShadow: "0px 20px 48px rgba(16, 24, 40, 0.2)",
        }}
      >
        {/* Header */}
        <MDBox
          bgColor={Colors.PRIMARY}
          px={3}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ height: pxToRem(64) }}
        >
          <ModalTitle title="Process Inquiry & Manage Lead" color="white" />
          <Icon
            sx={{ cursor: "pointer", color: "white" }}
            fontSize="medium"
            onClick={isBusy ? undefined : handleClose}
          >
            {Icons.CROSS}
          </Icon>
        </MDBox>

        {/* Modal Body */}
        <MDBox
          px={3}
          py={2.5}
          sx={{
            maxHeight: 520,
            overflowY: "auto",
          }}
        >
          {/* Customer Summary Header */}
          <MDBox
            p={2}
            sx={{
              backgroundColor: "#F9FAFB",
              borderRadius: pxToRem(8),
              border: "1px solid #EAECF0",
              mb: 2,
            }}
          >
            <MDTypography variant="body2" sx={{ fontSize: pxToRem(14), color: "#1D2939" }}>
              Inquiry <strong>{inquiry?.inquiryId ? `INQ-${inquiry.inquiryId}` : "-"}</strong> for{" "}
              <strong>{`${inquiry?.firstName || ""} ${inquiry?.lastName || ""}`.trim()}</strong>
            </MDTypography>
            <MDTypography
              variant="caption"
              sx={{ fontSize: pxToRem(13), color: "#475467", mt: 0.5, display: "block" }}
            >
              Mobile: <strong>{inquiry?.mobileNumber || "-"}</strong>
              {inquiry?.imd ? (
                <>
                  {" "}
                  &bull; IMD: <strong>{inquiry.imd}</strong>
                </>
              ) : null}
              {inquiry?.loanAmount ? (
                <>
                  {" "}
                  &bull; Amount:{" "}
                  <strong>
                    ₹
                    {Number(inquiry.loanAmount.toString().replace(/,/g, "")).toLocaleString(
                      "en-IN"
                    )}
                  </strong>
                </>
              ) : null}
            </MDTypography>
          </MDBox>

          {/* Form Fields: Loan Type and Optional Remarks */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel id="process-loan-type-label">Loan Type</InputLabel>
                <Select
                  labelId="process-loan-type-label"
                  id="process-loan-type"
                  value={selectedLoanType}
                  label="Loan Type"
                  onChange={(e) => setSelectedLoanType(e.target.value)}
                  sx={{ height: 42 }}
                >
                  {Object.entries(LOAN_TYPE_LABELS).map(([key, label]) => (
                    <MenuItem key={key} value={key}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={2}
                label="Reason Details / Remarks (Optional)"
                placeholder="Enter details or reason (saved when selecting Rejected by Bank or Denied by Customer)..."
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </Grid>
          </Grid>

          {/* Flow Guidance */}
          <MDBox
            p={1.5}
            sx={{
              backgroundColor: "#F0F9FF",
              borderRadius: pxToRem(8),
              border: "1px solid #B2DDFF",
            }}
          >
            <MDTypography
              variant="caption"
              sx={{
                fontSize: pxToRem(12),
                color: "#026AA2",
                fontWeight: 700,
                display: "block",
                mb: 0.5,
              }}
            >
              ℹ️ Choose an action to manage this lead:
            </MDTypography>
            <MDTypography
              variant="caption"
              sx={{ fontSize: pxToRem(12), color: "#175CD3", lineHeight: 1.5, display: "block" }}
            >
              &bull; <strong>Process Lead Successfully:</strong> Redirects to the respective lead
              form with pre-filled details to complete application.
              <br />
              &bull; <strong>Rejected by Bank:</strong> Saves status as Rejected by Bank in Inquiry
              without redirecting.
              <br />
              &bull; <strong>Denied by Customer:</strong> Saves status as Denied by Customer in
              Inquiry without redirecting.
            </MDTypography>
          </MDBox>
        </MDBox>

        {/* Modal Footer with the 3 Button Options */}
        <MDBox
          px={3}
          py={2}
          sx={{
            borderTop: "1px solid #EAECF0",
            backgroundColor: "#FAFAFA",
            display: "flex",
            flexDirection: { xs: "column-reverse", sm: "row" },
            alignItems: "center",
            justifyContent: "end",
            gap: 1.5,
          }}
        >
          <MDBox
            display="flex"
            flexWrap="wrap"
            gap={1}
            alignItems="center"
            justifyContent={{ xs: "stretch", sm: "flex-end" }}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            {/* 3. Denied by Customer */}
            <Button
              variant="contained"
              onClick={handleDeniedByCustomer}
              disabled={isBusy}
              startIcon={<BlockIcon fontSize="small" sx={{ color: "#FFFFFF !important" }} />}
              sx={{
                textTransform: "none",
                backgroundColor: "#DC6803",
                color: "#FFFFFF",
                fontWeight: 600,
                fontSize: pxToRem(13),
                px: 1.75,
                py: 0.9,
                borderRadius: pxToRem(6),
                boxShadow: "none",
                "&:hover": { backgroundColor: "#B54708", boxShadow: "none" },
              }}
            >
              {actionType === "denied_customer" ? "Saving..." : "Denied by Customer"}
            </Button>

            {/* 2. Rejected by Bank */}
            <Button
              variant="contained"
              onClick={handleRejectedByBank}
              disabled={isBusy}
              startIcon={
                <CancelOutlinedIcon fontSize="small" sx={{ color: "#FFFFFF !important" }} />
              }
              sx={{
                textTransform: "none",
                backgroundColor: "#D92D20",
                color: "#FFFFFF",
                fontWeight: 600,
                fontSize: pxToRem(13),
                px: 1.75,
                py: 0.9,
                borderRadius: pxToRem(6),
                boxShadow: "none",
                "&:hover": { backgroundColor: "#B42318", boxShadow: "none" },
              }}
            >
              {actionType === "rejected_bank" ? "Saving..." : "Rejected by Bank"}
            </Button>

            {/* 1. Process Lead Successfully */}
            <Button
              variant="contained"
              onClick={handleProcessLead}
              disabled={isBusy}
              startIcon={<ArrowForwardIcon fontSize="small" sx={{ color: "#FFFFFF !important" }} />}
              sx={{
                textTransform: "none",
                backgroundColor: "#0047B3",
                color: "#FFFFFF",
                fontWeight: 600,
                fontSize: pxToRem(13),
                px: 2,
                py: 0.9,
                borderRadius: pxToRem(6),
                boxShadow: "none",
                "&:hover": { backgroundColor: "#003580", boxShadow: "none" },
              }}
            >
              {actionType === "process" ? "Processing..." : "Process Lead Successfully"}
            </Button>
          </MDBox>
        </MDBox>
      </MDBox>
    </Modal>
  );
}
